import React from 'react';

import { Button } from '@components/Button';
import { FormStepSectionTitle } from '@components/Forms/FormStepSectionTitle';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import clsx from 'clsx';
import Color from 'color';

const useStyles = makeMuiStyles((theme, common) => {
  const shallowPrimary = Color(theme.palette.primary.main).alpha(0.2).toString();
  return {
    formStepSectionContainer: {
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
      '$showSplitRight &': {
        flex: 'initial',
        width: 340,
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        '$showSplitRight &': {
          width: '100%',
        },
      },
    },
    formStepChildrenWrapper: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${shallowPrimary}`,
      borderTop: '0px',
      height: 'calc(100% - 48px)',
    },
    formStepDivider: {
      backgroundColor: shallowPrimary,
      height: 1,
    },
    formStepSectionSplitRightContainer: {
      backgroundColor: theme.palette.background.paper,
      flex: '1',
      flexShrink: 1,
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    formStepSectionWrapper: {
      ...common.flexColumn,
      rowGap: '1rem',
    },
    formStepRow: {
      ...common.flexInline,
      alignItems: 'stretch',
      columnGap: '1rem',
      [theme.breakpoints.down('sm')]: {
        ...common.flexColumn,
        rowGap: '0.5rem',
      },
    },
    nextButton: {
      borderRadius: 0,
      width: 90,
    },
    showSplitRight: {},
  };
});

export interface FormStepSectionProps {
  id: number;
  title: React.ReactNode;
  currentStep: number;
  hasNext?: boolean;
  onClickNext?: () => void;
  splitRightTitle?: string;
  splitRightChildren?: React.ReactNode;
  splitHalf?: boolean;
  required?: boolean;
  rightRequired?: boolean;
}

export const FormStepSection: React.FC<React.PropsWithChildren<FormStepSectionProps>> = (props) => {
  const {
    id,
    title,
    currentStep,
    children,
    hasNext = true,
    onClickNext,
    splitRightTitle,
    splitRightChildren,
    splitHalf,
    required = false,
    rightRequired = required,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const open = currentStep >= id;

  const showSplitRight = open && !!splitRightTitle;
  const fixedSplitLeft = open && !!splitRightTitle && !splitHalf;

  return (
    <div className={clsx(classes.formStepSectionWrapper, fixedSplitLeft && classes.showSplitRight)}>
      <div className={clsx(classes.formStepRow)}>
        <div className={clsx(classes.formStepSectionContainer)}>
          <FormStepSectionTitle id={id} title={title} active={open} required={open && required} />
          <div className={classes.formStepChildrenWrapper}>{open && children}</div>
        </div>

        {showSplitRight && (
          <div className={clsx(classes.formStepSectionSplitRightContainer)}>
            <FormStepSectionTitle title={splitRightTitle} active required={rightRequired} />
            <div className={classes.formStepChildrenWrapper}>{splitRightChildren}</div>
          </div>
        )}
      </div>

      {currentStep === id && hasNext && (
        <Button
          text={formStrings.next}
          color="primary"
          type="button"
          className={classes.nextButton}
          onClick={onClickNext}
        />
      )}
    </div>
  );
};

export const FormStepDivider = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.formStepDivider} />;
};
